import { Button, message } from "antd";
import { useEffect, useMemo, useState } from "react";

import { deliveryService } from "@/api/services/deliveryService";
import { fileService } from "@/api/services/fileService";
import MediaWrap from "@/views/components/MediaWrap";
import ZhEmptyMedia from "@/views/components/zhEmptyMedia";
import type { DeliveryVo, EvidenceMaterialInfo, File as EntityFile } from "@zhihaoscm/entity";
import { DELIVERY_STATE } from "@zhihaoscm/meta";
import type { TradeOrderData } from "../TradeOrderComponentPrimitives";

export interface ProveMaterialProps {
    items?: TradeOrderData[];
    deliveryVo?: DeliveryVo;
    onSaved?: () => void | Promise<void>;
}

type SingleMaterialKey = Exclude<keyof EvidenceMaterialInfo, "otherMaterialsFileIds">;
const MATERIALS: Array<{ key: SingleMaterialKey; label: string } | null> = [
    { key: "tonProveFileId", label: "吨位证明" },
    { key: "sandSourceProofFileId", label: "砂石来源证明" },
    { key: "loadingPortFileId", label: "到装货港图片/视频" },
    { key: "clearanceSaleFileId", label: "清仓图片/视频" },
    { key: "sixWaterGaugesOneFileId", label: "六处水尺-1" },
    { key: "sixWaterGaugesTwoFileId", label: "六处水尺-2" },
    { key: "sixWaterGaugesThreeFileId", label: "六处水尺-3" },
    null,
    { key: "sixWaterGaugesFourFileId", label: "六处水尺-4" },
    { key: "sixWaterGaugesFiveFileId", label: "六处水尺-5" },
    { key: "sixWaterGaugesSixFileId", label: "六处水尺-6" }
];

function isVideo(file?: EntityFile): boolean {
    return [".mp4", ".mov", ".mkv"].includes(String(file?.type || "").toLowerCase());
}

/** 旧版优先采用提货凭证，六处水尺缺失时回退到关联运单的离港准备凭证。 */
function getEvidenceInfo(deliveryVo?: DeliveryVo): EvidenceMaterialInfo {
    const evidence = deliveryVo?.delivery?.evidenceMaterialInfo || {};
    const lazb = deliveryVo?.transportOrderDetailsShip?.lazbInfo;
    return {
        ...evidence,
        sixWaterGaugesOneFileId: evidence.sixWaterGaugesOneFileId || lazb?.sixWaterGaugesOneFileId,
        sixWaterGaugesTwoFileId: evidence.sixWaterGaugesTwoFileId || lazb?.sixWaterGaugesTwoFileId,
        sixWaterGaugesThreeFileId: evidence.sixWaterGaugesThreeFileId || lazb?.sixWaterGaugesThreeFileId,
        sixWaterGaugesFourFileId: evidence.sixWaterGaugesFourFileId || lazb?.sixWaterGaugesFourFileId,
        sixWaterGaugesFiveFileId: evidence.sixWaterGaugesFiveFileId || lazb?.sixWaterGaugesFiveFileId,
        sixWaterGaugesSixFileId: evidence.sixWaterGaugesSixFileId || lazb?.sixWaterGaugesSixFileId
    };
}

/** 提货凭证严格沿用 Vue 的四列顺序、文件回显与“其他材料”区域。 */
export default function ProveMaterial({ deliveryVo, onSaved }: ProveMaterialProps) {
    const evidence = useMemo(() => getEvidenceInfo(deliveryVo), [deliveryVo]);
    const [files, setFiles] = useState<Map<number, EntityFile>>(new Map());
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState<EvidenceMaterialInfo>(evidence);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const ids = [
            ...new Set([
                ...MATERIALS.flatMap(item => (item && evidence[item.key] ? [Number(evidence[item.key])] : [])),
                ...(evidence.otherMaterialsFileIds || []).map(Number)
            ])
        ].filter(Boolean);
        setDraft(evidence);
        if (!ids.length) {
            setFiles(new Map());
            return;
        }
        let disposed = false;
        void fileService
            .getFileByIds(ids)
            .then(result => {
                if (!disposed) setFiles(new Map(result.map(file => [Number(file.id), file])));
            })
            .catch(() => {
                // 旧版接口失败时仍保留凭证占位布局，不能让详情页产生未处理 Promise。
                if (!disposed) setFiles(new Map());
            });
        return () => {
            disposed = true;
        };
    }, [evidence]);

    /** 每个输入先上传文件，再把返回 ID 写入与 Vue EvidenceMaterialInfo 相同的字段。 */
    const upload = async (inputFiles: FileList | null, key: SingleMaterialKey | "otherMaterialsFileIds") => {
        if (!inputFiles?.length) return;
        try {
            const uploaded = await Promise.all(
                Array.from(inputFiles).map(async file => {
                    const form = new FormData();
                    form.append("file", file);
                    return fileService.uploadFile(form);
                })
            );
            setFiles(current => {
                const next = new Map(current);
                uploaded.forEach(file => next.set(Number(file.id), file));
                return next;
            });
            setDraft(current =>
                key === "otherMaterialsFileIds"
                    ? { ...current, otherMaterialsFileIds: uploaded.map(file => Number(file.id)) }
                    : { ...current, [key]: Number(uploaded[0].id) }
            );
        } catch {
            message.error("凭证上传失败");
        }
    };

    const save = async () => {
        if (!deliveryVo?.delivery?.id) return;
        setSaving(true);
        try {
            await deliveryService.updateFile(deliveryVo.delivery.id, draft);
            message.success("凭证保存成功");
            setEditing(false);
            await onSaved?.();
        } catch {
            message.error("凭证保存失败");
        } finally {
            setSaving(false);
        }
    };

    const canUpload = Number(deliveryVo?.delivery?.state) !== DELIVERY_STATE.CANCELED.code;
    /** Vue upload-video-img 使用整块上传入口，文件控件本身始终隐藏。 */
    const renderUploadTile = (key: SingleMaterialKey | "otherMaterialsFileIds", file?: EntityFile) => (
        <label className="trade-order-proof-material__upload" aria-label={`上传${key}`}>
            {file?.path ? (
                isVideo(file) ? (
                    <video src={file.path} muted />
                ) : (
                    <img alt="已上传凭证" src={file.path} />
                )
            ) : (
                <span>
                    <i aria-hidden="true" className="iconfont icon-shangchuan" />
                    上传视频或图片
                </span>
            )}
            <input
                hidden
                accept="image/*,video/*"
                multiple={key === "otherMaterialsFileIds"}
                type="file"
                onChange={event => void upload(event.target.files, key)}
            />
        </label>
    );
    return (
        <section className="section-card mb-4 trade-order-proof-material">
            <div className="flex items-center justify-between mb-4">
                <div className="section-title mb-0">佐证材料</div>
                {canUpload && !editing ? (
                    <Button type="primary" onClick={() => setEditing(value => !value)}>
                        上传
                    </Button>
                ) : editing ? (
                    <div className="trade-order-proof-material__actions">
                        <Button loading={saving} type="primary" onClick={() => void save()}>
                            保存
                        </Button>
                        <Button disabled={saving} onClick={() => setEditing(false)}>
                            取消
                        </Button>
                    </div>
                ) : null}
            </div>
            <div className="trade-order-proof-material__grid">
                {MATERIALS.map((item, index) => {
                    if (!item) return <div key={`placeholder-${index}`} />;
                    const file = files.get(Number((editing ? draft : evidence)[item.key]));
                    return (
                        <div key={item.key} className="trade-order-proof-material__item">
                            {editing ? (
                                <>
                                    <div className="trade-order-proof-material__label">{item.label}</div>
                                    {renderUploadTile(item.key, file)}
                                </>
                            ) : (
                                <MediaWrap
                                    height="135px"
                                    label={item.label}
                                    mediaType={isVideo(file) ? "video" : "image"}
                                    mediaUrl={file?.path}
                                    width="240px"
                                />
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="trade-order-proof-material__other">
                <div className="mb-2">其他材料</div>
                <div className="trade-order-proof-material__other-list">
                    {editing ? (
                        renderUploadTile(
                            "otherMaterialsFileIds",
                            files.get(Number(draft.otherMaterialsFileIds?.[0]))
                        )
                    ) : evidence.otherMaterialsFileIds?.length ? (
                        (editing ? draft.otherMaterialsFileIds : evidence.otherMaterialsFileIds)?.map(id => {
                            const file = files.get(Number(id));
                            return (
                                <MediaWrap
                                    key={id}
                                    height="135px"
                                    label=""
                                    mediaType={isVideo(file) ? "video" : "image"}
                                    mediaUrl={file?.path}
                                    width="240px"
                                />
                            );
                        })
                    ) : (
                        <ZhEmptyMedia height="135px" width="240px" />
                    )}
                </div>
            </div>
        </section>
    );
}
